import { useEffect, useState } from 'react';
import React from 'react';
import { CheckCircleTwoTone } from '@ant-design/icons';
import { Accordion } from 'react-bootstrap';
import StbButton from '../../../../atoms/button/StbButton';
import AsyncStbButton from '../../../../atoms/button/AsyncStbButton';
import { postApiSharepointUploadPhotoProjectId } from '../../../../../api/generated/endpoints/share-point/share-point';
import { getPreWorkControl, putPreWorkControl, signPreWorkControl, getPreWorkControlPdf } from '../../../../../api/workPreparationClient';
import type { PreWorkControl } from '../../../../../api';
import { toastError, toastSuccess } from '../../../../../utilities/messageUtils';
import './PreStartCheckAccordionItems.css';
import { CameraOutlined } from '@ant-design/icons';
import { useAccount } from '@azure/msal-react';

interface PreStartCheckAccordionItemsProps {
  workerView: boolean;
  projectId: number;
  isWeatherProtectionProject: boolean;
}

interface Checkpoint {
  approved: boolean;
  notApproved: boolean;
  remark: string;
}

const CHECKPOINT_LABELS = [
  'Montageplats åtkomlig',
  'Montageplats avröjd',
  'Montageplats grovplanerad',
  'Montageplats fri från is och snö',
  'Montageplats bärighet och avjämnad: För den lastklass som ställningen är avsedd för',
  'Monterings- och upplagsplats: I nära anslutning till montageplatsen',
  'Transportväg med tillräcklig bärighet: Finns mellan monterings- och upplagsplats och montageplats',
  'Arbetsberedning med riskanalys samt erforderliga KMA-/KM-dokument är upprättade och delgivna berörd personal',
];

const WEATHER_PROTECTION_CHECKPOINT = 'Montageplats väderskydd åtkomlig';
const WEATHER_PROTECTION_CHECKPOINT_LABELS = [
  ...CHECKPOINT_LABELS,
  WEATHER_PROTECTION_CHECKPOINT,
];

const getCheckpointLabels = (isWeatherProtectionProject: boolean): string[] =>
  isWeatherProtectionProject
    ? WEATHER_PROTECTION_CHECKPOINT_LABELS
    : CHECKPOINT_LABELS;

const emptyCheckpoints = (isWeatherProtectionProject: boolean): Checkpoint[] =>
  getCheckpointLabels(isWeatherProtectionProject).map(() => ({ approved: false, notApproved: false, remark: '' }));

const checkpointsFromData = (data: PreWorkControl): Checkpoint[] => [
  { approved: data.point1Approved ?? false, notApproved: false, remark: data.point1Remark ?? '' },
  { approved: data.point2Approved ?? false, notApproved: false, remark: data.point2Remark ?? '' },
  { approved: data.point3Approved ?? false, notApproved: false, remark: data.point3Remark ?? '' },
  { approved: data.point4Approved ?? false, notApproved: false, remark: data.point4Remark ?? '' },
  { approved: data.point5Approved ?? false, notApproved: false, remark: data.point5Remark ?? '' },
  { approved: data.point6Approved ?? false, notApproved: false, remark: data.point6Remark ?? '' },
  { approved: data.point7Approved ?? false, notApproved: false, remark: data.point7Remark ?? '' },
  { approved: data.point8Approved ?? false, notApproved: false, remark: data.point8Remark ?? '' },
  { approved: data.point9Approved ?? false, notApproved: false, remark: data.point9Remark ?? '' },
];

const checkpointsToPayload = (checkpoints: Checkpoint[]) => ({
  point1Approved: checkpoints[0].approved, point1Remark: checkpoints[0].remark,
  point2Approved: checkpoints[1].approved, point2Remark: checkpoints[1].remark,
  point3Approved: checkpoints[2].approved, point3Remark: checkpoints[2].remark,
  point4Approved: checkpoints[3].approved, point4Remark: checkpoints[3].remark,
  point5Approved: checkpoints[4].approved, point5Remark: checkpoints[4].remark,
  point6Approved: checkpoints[5].approved, point6Remark: checkpoints[5].remark,
  point7Approved: checkpoints[6].approved, point7Remark: checkpoints[6].remark,
  point8Approved: checkpoints[7].approved, point8Remark: checkpoints[7].remark,
  ...(checkpoints[8] ? { point9Approved: checkpoints[8].approved, point9Remark: checkpoints[8].remark } : {}),
});

const PreStartCheckAccordionItems: React.FC<PreStartCheckAccordionItemsProps> = ({
  workerView,
  projectId,
  isWeatherProtectionProject,
}) => {
  const activeAccount = useAccount();
  const [data, setData] = useState<PreWorkControl | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const checkpointLabels = getCheckpointLabels(isWeatherProtectionProject);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>(
    () => emptyCheckpoints(isWeatherProtectionProject)
  );
  const [showSignError, setShowSignError] = useState(false);

  const isSigned = data?.status === 'Klar';
  const signedDate = new Date().toLocaleDateString('sv-SE');
  const approvedCount = checkpoints.filter((c) => c.approved).length;
  const allDecided = checkpoints.every((c) => c.approved || c.notApproved);

  const updateCheckpoint = (index: number, field: keyof Checkpoint, value: boolean | string) => {
    setCheckpoints((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  };
  const [isUploading, setIsUploading] = useState(false);

  const signControl = async () => {
    if (!allDecided) {
      setShowSignError(true);
      return;
    }
    setShowSignError(false);

    const confirmed = window.confirm(
      'Är du säker? Detta skickar mail till arbetsledaren och kan inte ångras.'
    );
    if (!confirmed) return;

    try {
      await putPreWorkControl(projectId, {
        ...checkpointsToPayload(checkpoints),
        isSigned: false,
        status: 'Ej klar',
      });

      await signPreWorkControl(projectId);
      setData((prev) => prev ? { ...prev, isSigned: true, status: 'Klar' } : prev);

      await getPreWorkControlPdf(projectId);
      toastSuccess('Kontrollen sparad, signerad, mejl skickat och PDF skapad');
    } catch (err) {
      console.error(err);
      toastError('Kunde inte spara eller signera kontrollen');
    }
  };
  const handlePhotoUpload = async (index: number, file: File) => {
    try {
      setIsUploading(true);
      await postApiSharepointUploadPhotoProjectId(projectId, {
        File: file,
        RequirementTitle: checkpointLabels[index],
      });
      toastSuccess('Bilden har laddats upp.');
    } catch {
      toastError('Kunde inte ladda upp bilden.');
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getPreWorkControl(projectId);
        setData(res);
        setCheckpoints(checkpointsFromData(res).slice(0, checkpointLabels.length));
      } catch (err) {
        console.error(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId, isWeatherProtectionProject, checkpointLabels.length]);

  if (!workerView) return null;

  return (
    <Accordion.Item className="accordion-item shadow prestart-check-item" eventKey="0">
      <Accordion.Header>Kontroll innan påbörjat arbete
        {isSigned && <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: '25px', marginLeft: '10px' }} />}
      </Accordion.Header>
      <Accordion.Body className="prestart-check-body">
        {loading && <div>Laddar...</div>}
        {error && !loading && <div>Kunde inte hämta kontrollen.</div>}

        {data && (
          <div className="prestart-check-content">
            <p className="prestart-intro">
              Ställningsentreprenören ska innan påbörjande av arbetet kontrollera förutsättningarna enligt nedan.
            </p>

            <div className="pwc-progress">
              {approvedCount} av {checkpointLabels.length} godkända
            </div>

            <div className="pwc-checklist">
              {checkpointLabels.map((label, i) => (
                <div
                  key={i}
                  className={`pwc-checkpoint-row ${checkpoints[i].approved ? 'pwc-row-approved' : ''} ${showSignError && !checkpoints[i].approved && !checkpoints[i].notApproved ? 'pwc-row-missing' : ''}`}
                >
                  <div className="pwc-checkpoint-top">
                    <span className="pwc-checkpoint-num">{i + 1}.</span>
                    <span className="pwc-checkpoint-label">{label}</span>
                    {isSigned ? (
                      <div className="d-flex gap-2">
                        {checkpoints[i].approved && <span className="pwc-godkand-label">GODKÄND</span>}
                        {!checkpoints[i].approved && <span className="pwc-godkand-label">EJ GODKÄND</span>}
                      </div>
                    ) : (
                      <div className="d-flex gap-2 align-items-center">
                        <label className="pwc-approve-label">
                          <input type="checkbox" className="pwc-checkbox" checked={checkpoints[i].approved}
                            onChange={(e) => { updateCheckpoint(i, 'approved', e.target.checked); if (e.target.checked) updateCheckpoint(i, 'notApproved', false); }} />
                          <span className="pwc-godkand-label">GODKÄND</span>
                        </label>
                        <label className="pwc-approve-label">
                          <input type="checkbox" className="pwc-checkbox" checked={checkpoints[i].notApproved}
                            onChange={(e) => { updateCheckpoint(i, 'notApproved', e.target.checked); if (e.target.checked) updateCheckpoint(i, 'approved', false); }} />
                          <span className="pwc-godkand-label">EJ GODKÄND</span>
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="d-none"
                          disabled={isUploading}
                          onChange={(e) => { const file = e.target.files?.[0]; if (file) handlePhotoUpload(i, file); e.target.value = ''; }}
                        />
                        <StbButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          icon
                          aria-label="Ladda upp foto"
                          title="Ladda upp foto"
                          loading={isUploading}
                          disabled={isUploading}
                          onClick={(e) => {
                            const input = e.currentTarget.previousElementSibling as HTMLInputElement | null;
                            input?.click();
                          }}
                        >
                          <CameraOutlined aria-hidden="true" />
                        </StbButton>
                      </div>
                    )}
                  </div>
                  {!isSigned && (
                    <div className="pwc-checkpoint-remark">
                      <label className="pwc-remark-label">Anm:</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder=""
                        value={checkpoints[i].remark}
                        onChange={(e) => updateCheckpoint(i, 'remark', e.target.value)}
                      />
                    </div>
                  )}
                  {isSigned && checkpoints[i].remark && (
                    <div className="pwc-checkpoint-remark pwc-checkpoint-remark-text text-muted small">{checkpoints[i].remark}</div>
                  )}
                </div>
              ))}
            </div>

            {isSigned ? (
              <div className="ma-signed-info">
                Signerad av: {activeAccount?.name}
                <br />
                Datum: {signedDate}
              </div>
            ) : (
              <>
                <div className="stb-action-bar">
                  <AsyncStbButton
                    variant="primary"
                    onClick={signControl}
                    title={!allDecided ? `Alla ${checkpointLabels.length} punkter måste ha ett val (godkänd eller ej godkänd)` : ''}
                  >
                    Signera
                  </AsyncStbButton>
                </div>
                {showSignError && !allDecided && (
                  <div className="pwc-validation-msg pwc-validation-error">
                    Du måste välja godkänd eller ej godkänd för alla punkter innan du kan signera. Punkt {checkpoints
                      .map((c, i) => (!c.approved && !c.notApproved ? i + 1 : null))
                      .filter(Boolean)
                      .join(', ')} saknar val.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
};

export default PreStartCheckAccordionItems;
